import { Icon, type IconName } from "../components/Icon";
import { defineStrings, type Locale } from "../i18n";
import { percent, pluralForm, translator, unit } from "./shared";

const strings = defineStrings(
  {
    mastered: "{percent} mastered",
    upOne: "up {points} point",
    upOther: "up {points} points",
    downOne: "down {points} point",
    downOther: "down {points} points",
    steady: "steady",
    noData: "No data yet",
  },
  {
    mastered: "{percent} maîtrisé",
    upOne: "+{points}\u00a0point",
    upOther: "+{points}\u00a0points",
    downOne: "−{points}\u00a0point",
    downOther: "−{points}\u00a0points",
    steady: "stable",
    noData: "Pas encore de données",
  },
);

export interface SkillProgressFigureProps {
  readonly label: string;
  /** From the engine. Mastery: 0..1. Change: a difference of mastery, -1..1. */
  readonly value: number;
  readonly metric: "mastery" | "change";
  readonly locale: Locale;
}

type Direction = "up" | "down" | "steady";

const arrows: Readonly<Record<Direction, IconName>> = { up: "up", down: "down", steady: "steady" };

/**
 * A skill's mastery as a meter, or its change as signed percentage points with an arrow
 * and words. Values are clamped; NaN shows "No data yet".
 */
export function SkillProgressFigure(props: SkillProgressFigureProps) {
  const t = translator(strings, props.locale);
  if (!Number.isFinite(props.value)) {
    return (
      <div className="p-skill" data-metric={props.metric} data-empty="">
        <span className="p-skill__label">{props.label}</span>
        <span className="p-skill__empty">{t("noData")}</span>
      </div>
    );
  }

  if (props.metric === "mastery") {
    const value = unit(props.value) ?? 0;
    const shown = percent(props.locale, value);
    const whole = Math.round(value * 100);
    return (
      <div className="p-skill" data-metric="mastery">
        <span className="p-skill__label">{props.label}</span>
        <span className="p-skill__value">{shown}</span>
        <div
          className="p-meter"
          role="meter"
          aria-label={props.label}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={whole}
          aria-valuetext={t("mastered", { percent: shown })}
        >
          <span className="p-meter__fill" style={{ inlineSize: `${String(whole)}%` }} />
        </div>
      </div>
    );
  }

  const points = Math.round(Math.min(1, Math.max(-1, props.value)) * 100);
  const direction: Direction = points > 0 ? "up" : points < 0 ? "down" : "steady";
  const size = Math.abs(points);
  const form = pluralForm(props.locale, size);
  const words =
    direction === "steady"
      ? t("steady")
      : direction === "up"
        ? t(form === "one" ? "upOne" : "upOther", { points: size })
        : t(form === "one" ? "downOne" : "downOther", { points: size });
  return (
    <div className="p-skill" data-metric="change" data-direction={direction}>
      <span className="p-skill__label">{props.label}</span>
      <span className="p-skill__change">
        <Icon name={arrows[direction]} size={18} className="p-skill__arrow" />
        <span>{words}</span>
      </span>
    </div>
  );
}
